/* ==========================================================================
   FILE: api/cache.js
   IAMONEAI - Two-Tier Caching System
   Memory (fast) + localStorage (persistent)
   ========================================================================== */

// ==========================================================================
// CONFIGURATION
// ==========================================================================

const CACHE_CONFIG = {
  memoryMaxItems: 100,
  localStoragePrefix: 'iamoneai_cache_',
  defaultTTL: 300000, // 5 minutes
};

// ==========================================================================
// MEMORY CACHE
// ==========================================================================

const memoryCache = new Map();

// ==========================================================================
// CACHE ENTRY STRUCTURE
// ==========================================================================

/**
 * @typedef {Object} CacheEntry
 * @property {any} data - Cached data
 * @property {number} timestamp - When cached
 * @property {number} ttl - Time to live in ms
 * @property {boolean} persist - Also stored in localStorage
 */

// ==========================================================================
// CACHE METHODS
// ==========================================================================

const cache = {
  /**
   * Get cached data
   * @param {string} key - Cache key
   * @param {Object} options
   * @param {boolean} options.allowStale - Return expired data if available
   * @returns {any|null} Cached data or null
   */
  get(key, options = {}) {
    const { allowStale = false } = options;

    // Check memory first
    if (memoryCache.has(key)) {
      const entry = memoryCache.get(key);
      const isExpired = Date.now() > entry.timestamp + entry.ttl;

      if (!isExpired) {
        return entry.data;
      } else if (allowStale) {
        return entry.data;
      } else {
        // Clean up expired entry
        memoryCache.delete(key);
      }
    }

    // Check localStorage
    try {
      const stored = localStorage.getItem(CACHE_CONFIG.localStoragePrefix + key);
      if (stored) {
        const entry = JSON.parse(stored);
        const isExpired = Date.now() > entry.timestamp + entry.ttl;

        if (!isExpired) {
          // Restore to memory cache
          memoryCache.set(key, entry);
          return entry.data;
        } else if (allowStale) {
          return entry.data;
        } else {
          // Clean up expired entry
          localStorage.removeItem(CACHE_CONFIG.localStoragePrefix + key);
        }
      }
    } catch (err) {
      // localStorage might be unavailable
      console.warn('Cache localStorage error:', err);
    }

    return null;
  },

  /**
   * Set cached data
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} ttl - Time to live in milliseconds
   * @param {boolean} persist - Also store in localStorage
   */
  set(key, data, ttl = CACHE_CONFIG.defaultTTL, persist = false) {
    const entry = {
      data,
      timestamp: Date.now(),
      ttl,
      persist,
    };

    // Enforce memory limit (LRU eviction)
    if (memoryCache.size >= CACHE_CONFIG.memoryMaxItems) {
      const oldestKey = memoryCache.keys().next().value;
      memoryCache.delete(oldestKey);
    }

    // Store in memory
    memoryCache.set(key, entry);

    // Optionally persist to localStorage
    if (persist) {
      try {
        localStorage.setItem(
          CACHE_CONFIG.localStoragePrefix + key,
          JSON.stringify(entry)
        );
      } catch (err) {
        // localStorage might be full or unavailable
        console.warn('Cache localStorage set error:', err);
      }
    }
  },

  /**
   * Invalidate (delete) a cache entry
   * @param {string} key - Cache key
   */
  invalidate(key) {
    memoryCache.delete(key);
    try {
      localStorage.removeItem(CACHE_CONFIG.localStoragePrefix + key);
    } catch (err) {
      // Ignore
    }
  },

  /**
   * Invalidate all entries matching a pattern
   * @param {RegExp} pattern - Pattern to match keys
   */
  invalidatePattern(pattern) {
    // Memory cache
    for (const key of memoryCache.keys()) {
      if (pattern.test(key)) {
        memoryCache.delete(key);
      }
    }

    // localStorage
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_CONFIG.localStoragePrefix)) {
          const cacheKey = key.replace(CACHE_CONFIG.localStoragePrefix, '');
          if (pattern.test(cacheKey)) {
            keysToRemove.push(key);
          }
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (err) {
      // Ignore
    }
  },

  /**
   * Clear all cache entries
   */
  clear() {
    memoryCache.clear();

    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_CONFIG.localStoragePrefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (err) {
      // Ignore
    }
  },

  /**
   * Get cache statistics
   * @returns {Object} Cache stats
   */
  stats() {
    let localStorageCount = 0;
    let localStorageSize = 0;

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(CACHE_CONFIG.localStoragePrefix)) {
          localStorageCount++;
          localStorageSize += localStorage.getItem(key)?.length || 0;
        }
      }
    } catch (err) {
      // Ignore
    }

    return {
      memoryItems: memoryCache.size,
      memoryMaxItems: CACHE_CONFIG.memoryMaxItems,
      localStorageItems: localStorageCount,
      localStorageSizeBytes: localStorageSize * 2, // UTF-16
    };
  },

  /**
   * Check if key exists and is valid
   * @param {string} key 
   * @returns {boolean}
   */
  has(key) {
    return this.get(key) !== null;
  },
};

// ==========================================================================
// EXPORTS
// ==========================================================================

export { cache, CACHE_CONFIG };
export default cache;